import styled from 'styled-components';
import useResponsive from 'src/utils/device';

const DESKTOP_COLUMNS = [{ pattern: 'tall-short' }, { pattern: 'short-tall' }, { pattern: 'tall-short' }, { pattern: 'short-tall' }] as const;

const MOBILE_COLUMNS = [{ pattern: 'col-1' }, { pattern: 'col-2' }, { pattern: 'col-3' }] as const;

type Props = {
  hidden?: boolean;
  showBillboard?: boolean;
};

const SkeletonHighlight = ({ hidden = false, showBillboard = true }: Props) => {
  const responsive = useResponsive();
  const subMenuCount = responsive.isMobile ? 3 : 5;
  return (
    <SkeletonWrapper aria-hidden='true' $hidden={hidden}>
      <div className='sk-header'>
        <span className='sk-line' />
        <span className='sk sk-header-title' />
        <span className='sk-line' />
      </div>

      <div className='container'>
        <div className='isDesktop'>
          <div className='gallery-wrapper'>
            <div className='gallery-grid'>
              {DESKTOP_COLUMNS.map((col, colIndex) => (
                <div key={colIndex} className={`column ${col.pattern}`}>
                  <div className='gallery-item first'>
                    <span className='sk sk-fill' />
                  </div>
                  <div className='gallery-item second'>
                    <span className='sk sk-fill' />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---------------------- Mobile Gallery ----------------------- */}
        <div className='isMobile'>
          <div className='gallery-wrapper'>
            <div className='gallery-grid-mobile'>
              {MOBILE_COLUMNS.map((col, colIndex) => (
                <div key={colIndex} className={`column-mobile ${col.pattern}`}>
                  <div className='gallery-item item-1'>
                    <span className='sk sk-fill' />
                  </div>
                  <div className='gallery-item item-2'>
                    <span className='sk sk-fill' />
                  </div>
                  <div className='gallery-item item-3'>
                    <span className='sk sk-fill' />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---------------------- Sub Menu Buttons --------------------- */}
        <div className='body-list-sub-menu'>
          {Array.from({ length: subMenuCount }).map((_, i) => (
            <span key={i} className='sk sk-sub-menu-button' />
          ))}
        </div>

        {/* ---------------------- Billboard ad slot -------------------- */}
        {showBillboard && <div className='billboard-slot' />}
      </div>
    </SkeletonWrapper>
  );
};

const SkeletonWrapper = styled.div<{ $hidden: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  contain: layout style;

  margin-top: -15px;
  padding: 30px 0;
  padding-bottom: 0;

  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  transition:
    opacity 0.25s ease,
    visibility 0s linear ${({ $hidden }) => ($hidden ? '0.25s' : '0s')};
  pointer-events: none;

  .container {
    padding: 0;
    @media (max-width: 1024px) {
      padding: 0;
    }
  }

  /* ---------------------------- Shimmer base ---------------------------- */
  .sk {
    display: block;
    background: #e9e9e9;
    background-image: linear-gradient(90deg, #e9e9e9 0%, #f4f4f4 40%, #e9e9e9 80%);
    background-size: 200% 100%;
    animation: skShimmer 1.4s ease-in-out infinite;
    border-radius: 8px;
  }

  .sk-fill {
    width: 100%;
    height: 100%;
    border-radius: inherit;
  }

  @keyframes skShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .sk {
      animation: none;
      background-image: none;
    }
  }

  /* ------------------------------ Header -------------------------------- */
  .sk-header {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 0 30px;
    @media (max-width: 768px) {
      padding: 15px 0 30px;
    }
    .sk-line {
      flex: 1;
      height: 1px;
      background: #5f5f5f42;
    }
    .sk-header-title {
      width: 220px;
      height: 42px;
      flex: 0 0 auto;
      @media (max-width: 1024px) {
        width: 180px;
        height: 34px;
      }
      @media (max-width: 768px) {
        width: 140px;
        height: 26px;
      }
    }
  }

  /* --------------------------- Gallery wrapper --------------------------- */
  .gallery-wrapper {
    overflow: hidden;
    position: relative;
    @media (max-width: 768px) {
      padding: 0px 15px;
    }
  }

  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    height: 750px;
    margin: 2.5rem 0;
  }

  .gallery-grid-mobile {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    height: 450px;
  }

  /* --------------------------- Desktop columns --------------------------- */
  .column {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
  }

  .column.tall-short {
    .gallery-item.first {
      flex: 4;
    }
    .gallery-item.second {
      flex: 1.25;
    }
  }

  .column.short-tall {
    .gallery-item.first {
      flex: 1.25;
    }
    .gallery-item.second {
      flex: 4;
    }
  }

  .gallery-item {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    min-height: 0; /* กัน flex item ไม่ยอมหดใน column */
  }

  /* --------------------------- Mobile columns ---------------------------- */
  .column-mobile {
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    width: 100% !important;
  }

  .column-mobile.col-1 {
    .gallery-item.item-1 {
      flex: 4;
    }
    .gallery-item.item-2 {
      flex: 4;
    }
    .gallery-item.item-3 {
      flex: 2;
    }
  }

  .column-mobile.col-2 {
    .gallery-item.item-1 {
      flex: 2;
    }
    .gallery-item.item-2 {
      flex: 5;
    }
    .gallery-item.item-3 {
      flex: 3;
    }
  }

  .column-mobile.col-3 {
    .gallery-item.item-1 {
      flex: 4;
    }
    .gallery-item.item-2 {
      flex: 3;
    }
    .gallery-item.item-3 {
      flex: 3;
    }
  }

  /* ---------------------------- Sub menu row ----------------------------- */
  .body-list-sub-menu {
    display: flex;
    gap: 10px;
    margin-top: 1rem;
    justify-content: space-between;
    overflow: hidden;
    padding-bottom: 5px;
    @media (max-width: 768px) {
      padding: 0px 15px;
    }
  }

  .sk-sub-menu-button {
    flex: 1 1 0;
    max-width: 160px;
    /* ความสูง = padding 8px*2 + line-height ของ font 20px ≈ 46px (ตรงกับปุ่มจริง) */
    height: 46px;
    border-radius: 8px;
    @media (max-width: 768px) {
      height: 38px;
    }
  }

  /* --------------------------- Billboard slot ---------------------------- */
  /* ต้องตรงกับ .billboard-slot ใน SectionHighlight เป๊ะๆ */
  .billboard-slot {
    min-height: 250px;
    margin-top: 20px;
    @media (max-width: 768px) {
      min-height: 100px;
    }
  }

  /* ------------------------- Desktop/Mobile toggle ----------------------- */
  .isDesktop {
    display: block;
    @media (max-width: 768px) {
      display: none;
    }
  }

  .isMobile {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }

  @media (max-width: 768px) {
    padding: 0px 0;
    .gallery-item {
      border-radius: 12px;
    }
  }
`;

export default SkeletonHighlight;
